// CMS Data Service - API-first with FTP JSON fallback
// All editable content comes from the FastAPI backend so admin edits
// reflect immediately. Static FTP JSON is used only when the API is unreachable.

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.mt-productions.co.uk';

// Cache for loaded data
let showsCache = null;
let tourDatesCache = null;
let newsCache = null;
let historyCache = null;
let settingsCache = null;
let pagesCache = null;

// Maps each CMS file to the API endpoint that serves the same content.
// When an endpoint returns a list, it's wrapped to match the JSON structure.
const API_ENDPOINTS = {
  'shows.json':       { url: '/api/shows',                wrapKey: 'shows' },
  'tour_dates.json':  { url: '/api/tour-dates?includePast=true', wrapKey: 'tourDates' },
  'news.json':        { url: '/api/news',                 wrapKey: 'articles' },
  'history.json':     { url: '/api/history',              wrapKey: null },     // already shape-matching
  'settings.json':    { url: '/api/settings',             wrapKey: null },
  'pages.json':       { url: '/api/pages',                wrapKey: null },
  'about.json':       { url: '/api/about',                wrapKey: null },
  'privacy.json':     { url: '/api/privacy',              wrapKey: null },
};

async function loadJSON(filename) {
  // 1. Try the API first (cache-busted)
  const endpoint = API_ENDPOINTS[filename];
  if (endpoint) {
    try {
      const response = await fetch(`${API_URL}${endpoint.url}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        if (endpoint.wrapKey) {
          // Wrap list responses to match {key: [...]} structure
          return { [endpoint.wrapKey]: Array.isArray(data) ? data : (data[endpoint.wrapKey] || []) };
        }
        return data;
      }
      console.warn(`API ${endpoint.url} returned ${response.status}; falling back to static JSON`);
    } catch (err) {
      console.warn(`API ${endpoint.url} unreachable; falling back to static JSON`);
    }
  }

  // 2. Fallback to static FTP JSON
  try {
    const response = await fetch(`${PUBLIC_URL}/cms_data/${filename}`);
    if (!response.ok) throw new Error(`Failed to load ${filename}`);
    return await response.json();
  } catch (error) {
    console.error(`Error loading ${filename}:`, error);
    return null;
  }
}

// Allow components to force-refresh content (e.g. after admin saves)
export function clearCache() {
  showsCache = null;
  tourDatesCache = null;
  newsCache = null;
  historyCache = null;
  settingsCache = null;
  pagesCache = null;
}

// Shows
export async function getShows(options = {}) {
  if (!showsCache) {
    const data = await loadJSON('shows.json');
    showsCache = data?.shows || [];
  }
  
  let shows = [...showsCache].filter(s => s.id);
  
  // Apply filters
  if (options.status && options.status !== 'all') {
    shows = shows.filter(s => s.status === options.status);
  }
  if (options.featured !== undefined) {
    shows = shows.filter(s => s.featured === options.featured);
  }
  if (options.category) {
    shows = shows.filter(s => (s.category || 'family') === options.category);
  }

  return shows;
}

export async function getShowById(showId) {
  const shows = await getShows();
  return shows.find(s => s.id === showId) || null;
}

// Tour Dates
export async function getTourDates(options = {}) {
  if (!tourDatesCache) {
    const data = await loadJSON('tour_dates.json');
    tourDatesCache = data?.tourDates || [];
  }
  
  const shows = await getShows();
  const showsMap = Object.fromEntries(shows.map(s => [s.id, s]));
  
  let dates = [...tourDatesCache];
  
  // Filter out past dates unless includePast is true
  if (!options.includePast) {
    const today = new Date().toISOString().split('T')[0];
    dates = dates.filter(td => td.date >= today);
  }
  
  // Filter by show
  if (options.showId && options.showId !== 'all') {
    dates = dates.filter(td => td.showId === options.showId);
  }
  
  // Enrich with show data
  dates = dates.map(td => ({
    ...td,
    showTitle: showsMap[td.showId]?.title || '',
    showShortTitle: showsMap[td.showId]?.shortTitle || '',
    showPoster: showsMap[td.showId]?.poster || '',
    distance: null
  }));
  
  // Sort by date
  dates.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return (a.time || '').localeCompare(b.time || '');
  });
  
  return dates;
}

// Postcode search - uses external API for geocoding
export async function searchByPostcode(postcode, showId = null) {
  try {
    const params = new URLSearchParams();
    params.append('postcode', postcode);
    if (showId && showId !== 'all') params.append('showId', showId);
    
    console.log('Searching postcode:', postcode, 'URL:', `${API_URL}/api/tour-dates?${params}`);
    
    const response = await fetch(`${API_URL}/api/tour-dates?${params}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    console.log('API Response status:', response.status);
    
    if (!response.ok) throw new Error(`Search failed: ${response.status}`);
    const data = await response.json();
    
    console.log('API returned', data.length, 'results');
    
    // Get shows to enrich poster paths
    const shows = await getShows();
    const showsMap = Object.fromEntries(shows.map(s => [s.id, s]));
    
    // Enrich with local show data (for correct poster paths)
    return data.map(td => ({
      ...td,
      showPoster: showsMap[td.showId]?.poster || td.showPoster
    }));
  } catch (error) {
    console.error('Postcode search error:', error);
    // Fallback to local data without distance
    return getTourDates({ showId });
  }
}

// Search by coordinates (browser geolocation)
export async function searchByLocation(lat, lng, showId = null) {
  try {
    const params = new URLSearchParams();
    params.append('lat', lat.toString());
    params.append('lng', lng.toString());
    if (showId && showId !== 'all') params.append('showId', showId);
    
    console.log('Searching by location:', lat, lng);
    
    const response = await fetch(`${API_URL}/api/tour-dates?${params}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (!response.ok) throw new Error(`Search failed: ${response.status}`);
    const data = await response.json();
    
    console.log('API returned', data.length, 'results');
    
    // Get shows to enrich poster paths
    const shows = await getShows();
    const showsMap = Object.fromEntries(shows.map(s => [s.id, s]));
    
    // Enrich with local show data (for correct poster paths)
    return data.map(td => ({
      ...td,
      showPoster: showsMap[td.showId]?.poster || td.showPoster
    }));
  } catch (error) {
    console.error('Location search error:', error);
    // Fallback to local data without distance
    return getTourDates({ showId });
  }
}

// News
export async function getNews(options = {}) {
  if (!newsCache) {
    const data = await loadJSON('news.json');
    newsCache = data?.articles || [];
  }
  
  let articles = [...newsCache];
  
  // Filter by category
  if (options.category) {
    articles = articles.filter(a => a.category === options.category);
  }
  if (options.featured !== undefined) {
    articles = articles.filter(a => a.featured === options.featured);
  }
  
  // Sort by date descending
  articles.sort((a, b) => b.date.localeCompare(a.date));
  
  // Apply limit
  if (options.limit) {
    articles = articles.slice(0, options.limit);
  }
  
  return articles;
}

export async function getNewsById(articleId) {
  const articles = await getNews();
  return articles.find(a => a.id === articleId) || null;
}

// History
export async function getHistory() {
  if (!historyCache) {
    historyCache = await loadJSON('history.json');
  }
  
  if (Array.isArray(historyCache)) {
    return { timeline: historyCache, preHistory: null };
  }
  
  return {
    timeline: historyCache?.timeline || [],
    preHistory: historyCache?.preHistory || null
  };
}

// Settings
export async function getSettings() {
  if (!settingsCache) {
    settingsCache = await loadJSON('settings.json');
  }
  return settingsCache || {};
}

export async function getNavigation() {
  const settings = await getSettings();
  const nav = settings?.navigation || [];
  return [...nav].sort((a, b) => a.order - b.order);
}

// Pages
export async function getPage(pageName) {
  if (!pagesCache) {
    const data = await loadJSON('pages.json');
    pagesCache = data?.pages || {};
  }
  return pagesCache[pageName] || null;
}

// Stats (calculated from tour dates)
export async function getStats() {
  const allDates = await getTourDates({ includePast: true });
  const shows = await getShows();
  
  const today = new Date().toISOString().split('T')[0];
  const currentYear = new Date().getFullYear().toString();
  
  const futureDates = allDates.filter(td => td.date >= today);
  const pastDates = allDates.filter(td => td.date < today);
  const thisYearDates = allDates.filter(td => td.date.startsWith(currentYear));
  
  const uniqueVenues = new Set(allDates.map(td => `${td.venue}|${td.town}`)).size;
  const uniqueTowns = new Set(allDates.map(td => td.town)).size;
  const showsWithDates = new Set(futureDates.map(td => td.showId)).size;
  
  return {
    totalShowsThisYear: thisYearDates.length,
    remainingShows: futureDates.length,
    completedShows: pastDates.length,
    uniqueVenues,
    uniqueTowns,
    activeProductions: showsWithDates,
    nextShow: futureDates[0] || null
  };
}

export default {
  getShows,
  getShowById,
  getTourDates,
  searchByPostcode,
  searchByLocation,
  getNews,
  getNewsById,
  getHistory,
  getSettings,
  getNavigation,
  getPage,
  getStats,
  clearCache
};
